import { useState, useEffect } from "react";
import { Pressable, ActivityIndicator, View, Switch } from "react-native";
import styled from "styled-components/native";
import { useRouter } from "expo-router";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { useClub } from "@/contexts/ClubContext";
import { useAlert } from "@/hooks/useAlert";
import AlertSheet from "@/components/AlertSheet";
import { colors, radius, font } from "@/lib/theme";
import ContentWidth from "@/components/ContentWidth";
import { SlotListSkeleton } from "@/components/Skeleton";

const Section = styled.View`padding: 20px;`;
const Card = styled.View`background-color: ${colors.white}; border-radius: ${radius.lg}px; border: 1px solid ${colors.line}; padding: 20px;`;
const ToggleRow = styled.View`flex-direction: row; align-items: center; justify-content: space-between; margin-bottom: 8px;`;
const LaneRow = styled.View`flex-direction: row; align-items: center; justify-content: space-between;`;
const LaneLabel = styled.Text`font-size: 16px; font-weight: ${font.bold}; color: ${colors.ink};`;
const LaneSub = styled.Text`font-size: 13px; color: ${colors.inkMuted}; font-weight: ${font.medium}; margin-top: 8px;`;
const LaneInput = styled.TextInput`background-color: ${colors.bg}; border: 1px solid ${colors.line}; border-radius: ${radius.md}px; padding: 14px; font-size: 24px; font-weight: ${font.extrabold}; color: ${colors.ink}; text-align: center; width: 100px;`;
const SaveBtn = styled.View`background-color: ${colors.brand}; border-radius: ${radius.card}px; padding: 16px; align-items: center; margin-top: 24px;`;
const SaveBtnText = styled.Text`color: #fff; font-size: 16px; font-weight: ${font.bold};`;
const HintText = styled.Text`font-size: 13px; color: ${colors.inkMuted}; line-height: 20px; margin-top: 16px;`;

export default function NetSettingsScreen() {
  const router = useRouter();
  const { club } = useClub();
  const { alertProps, alert } = useAlert();
  const [enabled, setEnabled] = useState(false);
  const [lanes, setLanes] = useState("4");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!club?.id) return;
    async function load() {
      const snap = await getDoc(doc(db, "clubs", club!.id, "settings", "nets"));
      if (snap.exists()) {
        const data = snap.data();
        setEnabled(data.enabled !== false);
        setLanes(String(data.lanes || 4));
      }
      setLoading(false);
    }
    load();
  }, [club?.id]);

  const handleSave = async () => {
    if (!club) return;
    setSaving(true);
    await setDoc(doc(db, "clubs", club.id, "settings", "nets"), {
      enabled,
      lanes: parseInt(lanes) || 4,
    });
    setSaving(false);
    await alert({ title: "Saved!", emoji: "✅" });
    router.back();
  };

  if (loading) return <View style={{ flex: 1, backgroundColor: colors.bg }}><SlotListSkeleton /></View>;

  return (
    <ContentWidth style={{ flex: 1, backgroundColor: colors.bg }}>
      <Section>
        <Card>
          <ToggleRow>
            <LaneLabel>Enable net bookings</LaneLabel>
            <Switch
              value={enabled}
              onValueChange={setEnabled}
              trackColor={{ false: colors.line, true: colors.brandLight }}
              thumbColor={enabled ? colors.brand : colors.inkMuted}
            />
          </ToggleRow>
          <LaneSub style={{ marginTop: 0, marginBottom: enabled ? 20 : 0 }}>
            {enabled ? "Members will see net bookings on their club page." : "Net bookings are hidden from members."}
          </LaneSub>
          {enabled && (
            <>
              <LaneRow>
                <LaneLabel>Number of lanes</LaneLabel>
                <LaneInput value={lanes} onChangeText={setLanes} keyboardType="number-pad" maxLength={2} />
              </LaneRow>
              <LaneSub>Members can book up to {lanes || "4"} spots per hour slot.</LaneSub>
            </>
          )}
        </Card>
        {enabled && (
          <HintText>You can cancel any booking from the session screen with a reason. Members will be notified.</HintText>
        )}
        <Pressable onPress={handleSave} disabled={saving} style={({ pressed }) => ({ opacity: pressed ? 0.9 : 1 })}>
          <SaveBtn>{saving ? <ActivityIndicator color="#fff" /> : <SaveBtnText>Save</SaveBtnText>}</SaveBtn>
        </Pressable>
      </Section>
      <AlertSheet {...alertProps} />
    </ContentWidth>
  );
}
